import React from 'react';

interface AuthFieldProps {
  id: string;
  name: string;
  label: string;
  type?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  error?: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  required?: boolean;
  autoComplete?: string;
  helpText?: string;
}

// Dark-themed text input for auth screens. Default state, focus state and
// error state map to the same color tokens the public site uses (sky-500
// focus, red-400 error) so the look stays consistent across the app.
const AuthField: React.FC<AuthFieldProps> = ({
  id,
  name,
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  error,
  icon: Icon,
  required,
  autoComplete,
  helpText,
}) => (
  <div>
    <label
      htmlFor={id}
      className="block text-sm font-medium text-white/80 mb-2"
    >
      {label}
    </label>
    <div className="relative">
      {Icon && (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-5 w-5 text-white/40" aria-hidden="true" />
        </div>
      )}
      <input
        id={id}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        autoComplete={autoComplete}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : helpText ? `${id}-help` : undefined}
        className={`block w-full ${
          Icon ? 'pl-10' : 'pl-4'
        } pr-4 py-3 bg-white/[0.03] border ${
          error ? 'border-red-400/60' : 'border-white/10'
        } text-white placeholder-white/30 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/30 focus:outline-none transition-colors text-sm`}
      />
    </div>
    {error && (
      <p id={`${id}-error`} className="mt-2 text-sm text-red-400">
        {error}
      </p>
    )}
    {!error && helpText && (
      <p id={`${id}-help`} className="mt-2 text-xs text-white/50">
        {helpText}
      </p>
    )}
  </div>
);

export default AuthField;
